import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import i18n from "@/i18n";

// Catches render/commit crashes (notably those caused by the browser's automatic
// page translation mutating the DOM) and shows a calm, translated recovery screen
// instead of a blank white page.
export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("AppErrorBoundary caught an error", error, info);
  }

  setLang = (lng) => {
    try { localStorage.setItem("app_lang", lng); } catch (err) { /* noop */ }
    try { i18n.changeLanguage(lng); } catch (err) { /* noop */ }
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const t = i18n.t.bind(i18n);
    const current = (i18n.resolvedLanguage || i18n.language || "nl").split("-")[0];
    return (
      <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-6" data-testid="app-error-boundary">
        <div className="w-full max-w-md bg-white border border-zinc-200 rounded-sm p-8 text-center shadow-sm">
          <div className="w-12 h-12 rounded-full bg-[#0052FF]/10 text-[#0052FF] flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <h1 className="font-display text-2xl font-bold tracking-tight mb-2">{t("errorBoundary.title")}</h1>
          <p className="text-zinc-500 text-sm mb-6 leading-relaxed">{t("errorBoundary.message")}</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-[#0052FF] hover:bg-[#0040CC] text-white py-2.5 font-medium transition-colors"
            data-testid="error-retry-btn"
          >
            <RefreshCw className="w-4 h-4" /> {t("errorBoundary.retry")}
          </button>
          <div className="flex items-center justify-center gap-2 mt-5">
            <span className="text-xs text-zinc-400">{t("errorBoundary.language")}:</span>
            {["nl", "lt"].map((code) => (
              <button
                key={code}
                onClick={() => this.setLang(code)}
                className={`px-2 py-1 rounded-sm text-xs font-medium border transition ${
                  current === code
                    ? "bg-zinc-900 text-white border-zinc-900"
                    : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-100"
                }`}
                data-testid={`error-lang-${code}`}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }
}
